import React from 'react';
import { Helmet } from 'react-helmet';

import AuthorCredit from '../../components/ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../../components/ui/FreshnessDate';
import GEO_FAQ from '../../components/ui/GEO_FAQ';

export default function LandlordsJointOwnership() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "MTD for Landlords: Joint Ownership Thresholds",
    "dateModified": getFreshnessDateIso()
  };

  const faqs = [
    {
      question: "How do 50/50 jointly owned properties count towards MTD thresholds?",
      answer: "If you jointly own a property split 50/50, only your 50% share of the gross rental income counts towards your individual £50,000 Making Tax Digital threshold. You do not count the entire property revenue.",
      hmrc_manual_ref: "HMRC PIM1030: Property Income",
      hmrc_manual_url: "https://www.gov.uk/hmrc-internal-manuals/property-income-manual/pim1030"
    },
    {
      question: "Can one spouse be mandated for MTD while the other is exempt?",
      answer: "Yes. Making Tax Digital thresholds are assessed on an individual taxpayer basis, not per property. If one spouse has significant self-employment income pushing their total gross over £50k, they are mandated, while the other spouse may not be.",
      hmrc_manual_ref: "HMRC BIM42100: Business Expenses",
      hmrc_manual_url: "https://www.gov.uk/hmrc-internal-manuals/business-income-manual/bim42100"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans selection:bg-indigo-500/30">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <title>MTD for Jointly Owned Properties | Zap Expenses</title>
        <meta name="description" content="Joint property ownership MTD rules explained. Learn how to calculate your personal threshold and automate expense splitting for partners." />
        <meta name="keywords" content="MTD joint property, rental income split, HMRC PIM1030, MTD 2026 landlords, spouse tax threshold" />
      </Helmet>

      <div className="max-w-4xl mx-auto py-16 px-6">

        <FreshnessDate className="mb-4" />
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
          MTD rules for <span className="text-indigo-400">Jointly Owned Properties</span>
        </h1>
        
        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-12 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>For Making Tax Digital 2026, jointly owned property income is split strictly according to your ownership share before being tested against the £50,000 mandation threshold.</strong>
          </p>
        </div>

        <div className="prose prose-invert prose-lg max-w-none">
          <h2 className="text-2xl font-bold mb-4">Understanding the Split Threshold</h2>
          <p>
            The biggest confusion for landlords is calculating gross income. If you own a property 50/50 generating £60,000 in gross rent, your individual income is £30,000. Unless you have an additional £20,000 in self-employed income elsewhere, you fall below the £50,000 <strong>Making Tax Digital</strong> mandate for April 2026. This logic is covered extensively in <strong>HMRC</strong> manual PIM1030.
          </p>
          <p>
            However, splitting expenses correctly is equally vital. If you pay a repair bill, you must only claim your exact share of that expense. Sending property repair receipts via <strong>WhatsApp</strong> gives you a structured record to review; you or your accountant still apply the ownership split before filing.
          </p>

          <GEO_FAQ faqs={faqs} />

          <p className="mt-8">
            <a href="https://www.gov.uk/guidance/income-tax-when-you-rent-out-a-property-working-out-your-rental-income" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors font-bold">
              Check HMRC Joint Property rules →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
